/**
 * Admin Setup Service
 * Functions for promoting users to admin and managing admin users
 * 
 * NOTE: Admin promotion is data-driven via email check in userSync.js
 * This file provides manual admin management functions
 */

import { doc, getDoc, updateDoc, query, where, getDocs, collection } from 'firebase/firestore';
import { db } from '../../firebase';
import { serverTimestamp } from 'firebase/firestore';

const USERS_COLLECTION = 'users';

/**
 * Promote a user to admin by email
 * Sets role to 'admin' and plan to 'growth'
 * @param {string} email - User email to promote
 * @returns {Promise<Object>} Updated user document
 */
export async function promoteToAdminByEmail(email) {
  if (!email) {
    throw new Error('Email is required');
  }

  try {
    // Find user by email
    const usersRef = collection(db, USERS_COLLECTION);
    const q = query(usersRef, where('email', '==', email.toLowerCase()));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      throw new Error(`User with email ${email} not found`);
    }

    // Get first matching user (emails should be unique)
    const userDoc = querySnapshot.docs[0];
    const userId = userDoc.id;

    // Update user to admin
    const userRef = doc(db, USERS_COLLECTION, userId);
    await updateDoc(userRef, {
      role: 'admin',
      plan: 'growth',
      updatedAt: serverTimestamp(),
    });

    console.log(`Ô£à User ${email} promoted to admin`);
    
    return {
      uid: userId,
      email: email,
      role: 'admin',
      plan: 'growth',
    };
  } catch (error) {
    console.error('Error promoting user to admin:', error);
    throw error;
  }
}

/**
 * Promote a user to admin by UID
 * Sets role to 'admin' and plan to 'growth'
 * @param {string} uid - User ID to promote
 * @returns {Promise<Object>} Updated user document
 */
export async function promoteToAdminByUid(uid) {
  if (!uid) {
    throw new Error('User ID is required');
  }

  try {
    const userRef = doc(db, USERS_COLLECTION, uid);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      throw new Error(`User with uid ${uid} not found`);
    }

    await updateDoc(userRef, {
      role: 'admin',
      plan: 'growth',
      updatedAt: serverTimestamp(),
    });

    const userData = userDoc.data();
    console.log(`Ô£à User ${userData.email} (${uid}) promoted to admin`);
    
    return {
      uid: uid,
      email: userData.email,
      role: 'admin',
      plan: 'growth',
    };
  } catch (error) {
    console.error('Error promoting user to admin:', error);
    throw error;
  }
}

/**
 * Demote an admin user to regular user
 * Sets role to 'user' and plan to 'free'
 * @param {string} uid - User ID to demote
 * @returns {Promise<Object>} Updated user document
 */
export async function demoteFromAdmin(uid) {
  if (!uid) {
    throw new Error('User ID is required');
  }

  try {
    const userRef = doc(db, USERS_COLLECTION, uid);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      throw new Error(`User with uid ${uid} not found`);
    }

    await updateDoc(userRef, {
      role: 'user',
      plan: 'free',
      updatedAt: serverTimestamp(),
    });

    const userData = userDoc.data();
    console.log(`Ô£à User ${userData.email} (${uid}) demoted from admin`);
    
    return {
      uid: uid,
      email: userData.email,
      role: 'user',
      plan: 'free',
    };
  } catch (error) {
    console.error('Error demoting user from admin:', error);
    throw error;
  }
}

/**
 * Get all admin users
 * @returns {Promise<Array>} Array of admin user documents
 */
export async function getAllAdmins() {
  try {
    const usersRef = collection(db, USERS_COLLECTION);
    const q = query(usersRef, where('role', '==', 'admin'));
    const querySnapshot = await getDocs(q);

    const admins = [];
    querySnapshot.forEach((doc) => {
      admins.push({
        uid: doc.id,
        ...doc.data(),
      });
    });

    return admins;
  } catch (error) {
    console.error('Error getting admin users:', error);
    throw error;
  }
}

/**
 * Check if a user is admin
 * @param {string} uid - User ID
 * @returns {Promise<boolean>} True if user is admin
 */
export async function checkIsAdmin(uid) {
  if (!uid) {
    return false;
  }

  try {
    const userRef = doc(db, USERS_COLLECTION, uid);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      return false;
    }

    const userData = userDoc.data();
    return userData.role === 'admin';
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
}











